import { Box, ButtonBase, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, EnrichedEventData, OrderData } from '../../api';
import { DEFAULT_ORDER_PAGE_COLOR } from '../../common/theme/mbTokens';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPage } from '../../utils/AccountUtils';
import { CalendarItems } from '../calendarWrapper/CalendarWrapper';
import { SummaryCluster, SummarySlot } from '../utils/calendarSummary';
import { HOUR_HEIGHT, ItemCssDimension } from './CalendarCalculator';
import { formatSeconds, SummarySlotPopover } from './SummarySlotPopover';

type SummaryClusterBoxProps = {
  cluster: SummaryCluster;
  css?: ItemCssDimension;
  peakSlotCount: number;
  date: string;
  items: CalendarItems;
  openOrder: (event: any, order: OrderData) => void;
  openEvent: (event: EnrichedEventData) => void;
};

export const SummaryClusterBox = ({
  cluster,
  css,
  peakSlotCount,
  date,
  items,
  openOrder,
  openEvent,
}: SummaryClusterBoxProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const [openSlot, setOpenSlot] = useState<{ slot: SummarySlot; anchor: HTMLElement } | null>(null);

  if (!css) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: `${css.top}px`,
        height: `${css.height}px`,
        insetInlineStart: `${css.left}%`,
        width: `${css.width}%`,
        pointerEvents: 'none',
      }}>
      <Box
        sx={{
          position: 'absolute',
          inset: '2px',
          borderRadius: 1,
          bgcolor: 'action.hover',
          border: '1px dashed',
          borderColor: 'mb.control',
        }}
      />
      {cluster.slots.map((slot) => {
        const isSelected = openSlot?.slot.start === slot.start;
        return (
          <ButtonBase
            key={slot.start}
            aria-label={t('calendar.summary.slotAria', {
              time: formatSeconds(slot.start),
              count: slot.count,
              persons: slot.persons,
            })}
            onClick={(event) => {
              event.stopPropagation();
              setOpenSlot({ slot, anchor: event.currentTarget });
            }}
            sx={{
              position: 'absolute',
              pointerEvents: 'auto',
              top: `${(HOUR_HEIGHT * (slot.start - cluster.start_time)) / 3600 + 1}px`,
              height: `${HOUR_HEIGHT / 2 - 2}px`,
              insetInlineStart: { xs: '2px', sm: '3px' },
              width: { xs: 'calc(100% - 4px)', sm: 'calc(100% - 6px)' },
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 0.75 },
              px: { xs: 0.5, sm: 0.75 },
              bgcolor: 'mb.surface',
              border: '1px solid',
              borderColor: 'mb.line',
              borderRadius: '4px',
              overflow: 'hidden',
              '&:hover': { bgcolor: 'mb.ground' },
              ...(isSelected && { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '1px' }),
            }}>
            <Typography component="span" fontSize="12px" fontWeight={600} dir="ltr" noWrap sx={{ minWidth: 0 }}>
              {`×${slot.count}`}
              <Typography component="span" fontSize="inherit" fontWeight={400} color="text.secondary">
                {` · ${slot.persons}`}
              </Typography>
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0, height: 8 }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: '1px',
                  height: 8,
                  borderRadius: '2px',
                  overflow: 'hidden',
                  width: `${Math.round((slot.count / peakSlotCount) * 100)}%`,
                }}>
                {slot.types.map((type) => (
                  <Box
                    key={type.orderPageId}
                    sx={{
                      flex: type.count,
                      minWidth: 3,
                      bgcolor: getOrderPage(account, type.orderPageId)?.color ?? DEFAULT_ORDER_PAGE_COLOR,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </ButtonBase>
        );
      })}
      {openSlot && (
        <SummarySlotPopover
          anchorEl={openSlot.anchor}
          open
          onClose={() => setOpenSlot(null)}
          date={date}
          slot={openSlot.slot}
          items={items}
          openOrder={openOrder}
          openEvent={openEvent}
        />
      )}
    </Box>
  );
};
